// Single audio facade: owns the unlock gesture, wires music + SFX to game
// events (v1 had two disconnected stacks — Tone.js music and WebAudio SFX).

import type { EventBus } from '@/app/events';
import { MusicSystem } from './music';
import { SfxPlayer } from './sfx';

export class AudioEngine {
  readonly music = new MusicSystem();
  readonly sfx = new SfxPlayer();
  private unlocked = false;
  private environment = 'urban';

  constructor(
    bus: EventBus,
    muted: boolean,
    private readonly onMuteChange: (muted: boolean) => void,
  ) {
    this.music.isMuted = muted;

    bus.on('coin:collected', () => this.sfx.play('coinCollect', 0.5));
    bus.on('enemy:jumped', () => this.sfx.play('enemyDefeat', 0.7));
    bus.on('player:jumped', ({ double }) => this.sfx.play('jump', double ? 0.8 : 0.6));
    bus.on('player:roof-ride', () => this.sfx.play('carPass', 0.4));
    bus.on('combat:started', () => {
      this.sfx.play('combat', 0.7);
      this.music.onCombatStart();
    });
    bus.on('combat:ended', ({ victory }) => {
      this.music.onCombatEnd();
      if (victory) this.sfx.play('enemyDefeat', 0.9);
    });
    bus.on('player:hit', ({ fatal }) => {
      this.sfx.play('crash', fatal ? 0.9 : 0.6);
      if (fatal) {
        this.sfx.play('gameOver', 0.8);
        this.music.onGameOver();
      }
    });
  }

  /** First user gesture: unlock WebAudio + start music for the district. */
  async startRun(environment: string): Promise<void> {
    this.environment = environment;
    if (!this.unlocked) {
      this.unlocked = true;
      await this.sfx.unlock();
      this.sfx.play('gameStart', 0.7);
      await this.music.init(environment);
      return;
    }
    this.sfx.play('gameStart', 0.5);
    // Rebuild the generative track when the district (environment) changes
    await this.music.init(environment);
  }

  pause(): void {
    this.music.pause();
  }

  resume(): void {
    this.music.resume();
  }

  updateSpeed(gameSpeed: number): void {
    this.music.update(gameSpeed);
  }

  toggleMute(): boolean {
    const muted = !this.music.isMuted;
    this.music.setMuted(muted);
    this.onMuteChange(muted);
    return muted;
  }

  get isMuted(): boolean {
    return this.music.isMuted;
  }

  get currentEnvironment(): string {
    return this.environment;
  }
}
